// The candidate summary (lane B7 S14; CR-007 "System compiles submitted scorecards, coverage, flags and summary;
// Recruiter reviews.", CR-025 the Recruiter's release and the versioned notes). The page shows the readiness verdict
// (Ready or Not Ready with every gap named), coverage of the ROOTS values, the flags, the scorecards (sealed until the
// reveal), the Recruiter's release and the notes. Plan D13: "Make Recruiter review mandatory."; new evidence after a
// release makes it stale and it returns to Recruiter Review; "Debrief participants see released summary." The
// Coordinator's projection is the status alone: no score, no flag and no note.
// B14 (R5, DC-249): the *Scoring summary & rubric* block (PlaybookRubric) reads the snapshot's average core score over
// the five values, its band and the rubric lines (C3); a tally line counts each recommendation (the earlier-scale word
// only when such a card exists); the flags gain Consider Other Role. A snapshot frozen before B14 lacks these keys: the
// block draws "—" and nothing is back-filled.

import { useState } from "react";
import {
  summaryOpen, summaryReadinessCheck, summaryRelease, summarySaveNotes, type SummaryOpenOutput, type SummaryReadinessCheckOutput,
} from "@/api/tools";
import { errorText, isClientRefusal, useRead, useWrite } from "@/api/useTool";
import {
  ACTION_LABELS, Banner, Card, CoverageGrid, DEBRIEF_READINESS_STATUS, ErrorStrip, Gate, LEVEL_RULE_LABELS,
  LOOP_LABELS, PACKET_STATUS, PageHeader, Pill, PLAYBOOK_LABELS, PlaybookRubric, RECOMMENDATION_LABELS, ROOTS_LABELS, ROOTS_ORDER,
  SCORECARD_LABELS, SUMMARY_STATUS, toneOf,
  type CoverageGroup, type CoverageRound, type GateRow,
} from "@/mc";
import { ROOM_RULES } from "../room/labels";
import { hisRoundLabel, isLoopRound } from "../score/round";
import { cardsFromSnapshot, RevealGrid } from "../room/RevealGrid";
import { viewerOf, type NoteOut } from "../room/types";
import { averageOf, bandOf, countOf, recoWord, tallyOf, type RubricBand } from "../room/reco";
import { SUMMARY_LABELS, SUMMARY_RULES } from "./labels";
import { Notes, type NoteMode } from "./Notes";

/** S06's release and notes on the sheet read (vocab RELEASE_KINDS, NOTE_KINDS): the generated read carries them as
 * open objects, read here by their fields. */
export interface SheetAdditions {
  release?: { kind?: "released" | "stale" | string; released_at?: string | null; released_by?: string | null; version?: number | null } | null;
  notes?: NoteOut | null;
}
export type Sheet = SummaryOpenOutput & SheetAdditions;


const ROOTS_WORD: Readonly<Record<string, string>> = {
  respect: ROOTS_LABELS.respect, ownership: ROOTS_LABELS.ownership, opportunity: ROOTS_LABELS.opportunity,
  together: ROOTS_LABELS.together, service: ROOTS_LABELS.service,
};

/** The summary chain (G §11): Waiting for Inputs → Recruiter Review → Ready for Debrief / Blocked. */
export function summaryStatusOf(sheet: Sheet, check?: SummaryReadinessCheckOutput | null): string {
  if (sheet.status === "blocked") return SUMMARY_STATUS.blocked;
  if (sheet.release?.kind === "released") return SUMMARY_STATUS.readyForDebrief;
  if (sheet.release?.kind === "stale") return SUMMARY_STATUS.recruiterReview;
  if (!sheet.sheet_id || (check && !check.ready)) return SUMMARY_STATUS.waitingForInputs;
  return SUMMARY_STATUS.recruiterReview;
}

/** A loop round not held: his round wording when the label names an onsite round, else the loop and the label. */
const loopRound = (label: string | undefined) =>
  isLoopRound(label) ? hisRoundLabel(label ?? "") : [LOOP_LABELS.finalInterviewLoop, label].filter(Boolean).join(" · ");

const nameOf = (row: Record<string, unknown>, ...keys: string[]) => keys.map((k) => row[k]).find((v) => typeof v === "string") as string | undefined;

/** Every gap of the compile and reveal check in his words; existence only for concerns, never a score. */
export function checkRows(check: SummaryReadinessCheckOutput): GateRow[] {
  return [
    ...check.rounds_not_held.map((r) => ({ status: "bad" as const, text: loopRound(nameOf(r, "round", "label")) })),
    ...check.scorecards_outstanding.map((r) => ({ status: "bad" as const, text: [SUMMARY_LABELS.scorecards, hisRoundLabel(nameOf(r, "author", "name", "round"))].filter(Boolean).join(" · ") })),
    ...check.values_uncovered.map((v) => ({ status: "bad" as const, text: [SUMMARY_LABELS.coverage, ROOTS_WORD[v] ?? v].join(" · ") })),
    ...check.evidence_unconfirmed.map((r) => ({ status: "warn" as const, text: [SCORECARD_LABELS.evidence, nameOf(r, "author", "name")].filter(Boolean).join(" · ") })),
    ...(check.concerns_open > 0 ? [{ status: "warn" as const, text: LEVEL_RULE_LABELS.concernFlag, count: check.concerns_open }] : []),
  ];
}

/** Coverage of the five ROOTS values by round, from the snapshot (value → [{round, author}]). */
export function coverageOf(snapshot: Record<string, unknown>): { rounds: CoverageRound[]; groups: CoverageGroup[] } {
  const coverage = (snapshot.coverage ?? {}) as Record<string, { round?: string }[]>;
  const roundKeys = [...new Set(Object.values(coverage).flatMap((by) => (by ?? []).map((c) => c.round ?? "")).filter(Boolean))];
  const rounds = roundKeys.map((key) => ({ key, label: hisRoundLabel(key) }));
  const rows = ROOTS_ORDER.map((value) => {
    const by = new Set((coverage[value] ?? []).map((c) => c.round ?? ""));
    return {
      key: value, label: ROOTS_WORD[value] ?? value, required: true,
      cells: Object.fromEntries(roundKeys.map((r) => [r, by.has(r) ? ("covered" as const) : ("open" as const)])),
    };
  });
  return { rounds, groups: [{ label: ROOTS_LABELS.roots, rows }] };
}

/** The flag words a sheet carries: a Concern flag, VP+, a section not assessed. */
export function flagWords(sheet: Sheet): string[] {
  const snapshot = (sheet.snapshot ?? {}) as Record<string, unknown>;
  const flags = (snapshot.flags ?? {}) as Record<string, unknown>;
  const cards = cardsFromSnapshot(sheet.snapshot);
  const words: string[] = [];
  const any = (kinds: string[]) => cards.some((c) => c.flags.some((f) => kinds.includes(f)));
  if (any(["no_hire_recommendation", "two_low_ratings", "legacy_two_2s"]) || flags.two_2s) words.push(LEVEL_RULE_LABELS.concernFlag);
  if (any(["vp_no_hire", "vp_low_rating"])) words.push(LEVEL_RULE_LABELS.vpPlus);
  if (flags.not_assessed) words.push(SCORECARD_LABELS.notAssessed);
  if (flags.consider_other_role === true) words.push(RECOMMENDATION_LABELS.considerOtherRole);
  return words;
}

/** The consolidated *Scoring summary & rubric* (C3): every key optional, a frozen snapshot reads null / 0 / []. */
export interface PanelScoring {
  average: string | null;
  valuesScored: number;
  band: RubricBand | null;
  lines: string[];
  tally: { value: string; count: number }[] | null;
}

export function scoringOf(snapshot: Record<string, unknown> | null | undefined): PanelScoring {
  const rubric = (snapshot?.rubric ?? null) as { lines?: unknown } | null;
  const lines = Array.isArray(rubric?.lines) ? rubric.lines.filter((l): l is string => typeof l === "string") : [];
  return {
    average: averageOf(snapshot?.core_average),
    valuesScored: countOf(snapshot?.core_values_scored) ?? 0,
    band: bandOf(snapshot?.core_band),
    lines,
    tally: tallyOf(snapshot?.tally),
  };
}

/** "Strong Hire 1 · Hire 2 · Consider Other Role 0 · No Hire 1" (+ the earlier-scale word when such a card exists);
 * "—" when the snapshot carries no tally. */
export function tallyLine(tally: PanelScoring["tally"]): string {
  if (!tally) return "—";
  return tally.flatMap(({ value, count }) => {
    const word = recoWord(value);
    return word ? [`${word.label} ${count}`] : [];
  }).join(" · ");
}

/** The summary's scoring block: the average core score over the five values, the rubric with its band, the tally. */
export function PanelScoringCard({ snapshot }: { snapshot: Record<string, unknown> | null }) {
  const scoring = scoringOf(snapshot);
  return (
    <Card title={PLAYBOOK_LABELS.scoringSummaryRubric}>
      <div className="mc-stack">
        <PlaybookRubric
          average={scoring.average}
          valuesScored={scoring.valuesScored}
          ofValues={5}
          lines={scoring.lines}
          band={scoring.band}
          heading={PLAYBOOK_LABELS.averageCoreScore}
          scoringSummaryText={PLAYBOOK_LABELS.averageCoreScore}
        />
        <p className="mc-hint">{tallyLine(scoring.tally)}</p>
      </div>
    </Card>
  );
}

export interface SummaryBodyProps {
  sheet: Sheet;
  check?: SummaryReadinessCheckOutput | null | undefined;
  onRelease?: (() => void) | undefined;
  releasing?: boolean | undefined;
  onSaveNotes?: ((body: string, mode: NoteMode) => void) | undefined;
  savingNotes?: boolean | undefined;
}

/** The page without its reads: what the tests render. */
export function SummaryBody({ sheet, check, onRelease, releasing, onSaveNotes, savingNotes }: SummaryBodyProps) {
  const word = summaryStatusOf(sheet, check);
  const viewer = viewerOf(sheet.projection);
  const released = sheet.release?.kind === "released";
  const stale = sheet.release?.kind === "stale";
  const header = (
    <PageHeader
      title={SUMMARY_LABELS.candidateSummary}
      pills={<>
        <Pill tone={toneOf(word as Parameters<typeof toneOf>[0])}>{word}</Pill>
        {released && viewer !== "coordinator" ? <Pill tone="ok">{PACKET_STATUS.released}</Pill> : null}
      </>}
      meta={viewer === "coordinator" ? [] : [sheet.candidate]}
    />
  );
  if (viewer === "coordinator") {
    return <div className="mc-stack">{header}<Banner tone="info">{ROOM_RULES.coordinatorNoVote}</Banner></div>;
  }
  const snapshot = sheet.snapshot as Record<string, unknown> | null;
  const cover = snapshot ? coverageOf(snapshot) : null;
  const flags = flagWords(sheet);
  const canRelease = sheet.can.release === true;
  return (
    <div className="mc-stack">
      {header}
      {stale ? <Banner tone="warn">{SUMMARY_RULES.recruiterReviewMandatory}</Banner> : null}
      {!released && !canRelease ? <p className="mc-hint">{SUMMARY_RULES.participantsSeeReleased}</p> : null}
      {check ? (
        <Gate
          title={SUMMARY_LABELS.candidateSummary}
          verdict={check.ready ? DEBRIEF_READINESS_STATUS.ready : DEBRIEF_READINESS_STATUS.notReady}
          ready={check.ready}
          rows={checkRows(check)}
          mode={canRelease && !released ? "edit" : "view"}
          action={{ label: ACTION_LABELS.release, onClick: onRelease, busy: releasing, hs: "summary.release" }}
        />
      ) : null}
      {cover && cover.rounds.length > 0 ? (
        <Card title={SUMMARY_LABELS.coverage}>
          <CoverageGrid label={SUMMARY_LABELS.coverage} rounds={cover.rounds} groups={cover.groups} />
        </Card>
      ) : null}
      {flags.length > 0 ? (
        <Card title={SUMMARY_LABELS.flags} count={flags.length}>
          <div>{flags.map((f) => <Pill key={f} tone={f === LEVEL_RULE_LABELS.vpPlus ? "red" : "flag"}>{f}</Pill>)}</div>
        </Card>
      ) : null}
      {snapshot && sheet.revealed_at ? <PanelScoringCard snapshot={snapshot} /> : null}
      <RevealGrid cards={cardsFromSnapshot(sheet.snapshot)} sealed={!sheet.revealed_at} />
      <Notes
        title={SUMMARY_LABELS.summary}
        note={sheet.notes ?? null}
        canWrite={sheet.can.save_notes === true}
        busy={savingNotes}
        onSave={onSaveNotes}
        saveHs="summary.notes.save"
        
      />
    </div>
  );
}

export interface SummaryPageProps { rid: string; cid: string; applicationId: string }

export function SummaryPage({ rid, cid, applicationId }: SummaryPageProps) {
  const input = { requisition_id: rid, application_id: applicationId, cycle_id: cid };
  const sheetKey = ["summary.open", rid, applicationId, cid] as const;
  const checkKey = ["summary.readiness_check", rid, applicationId, cid] as const;
  const read = useRead(sheetKey, () => summaryOpen(input));
  const sheet = read.data as Sheet | undefined;
  const check = useRead(checkKey, () => summaryReadinessCheck(input), { enabled: !!sheet && viewerOf(sheet.projection) !== "coordinator" });
  const invalidate = [sheetKey, checkKey, ["debrief.open", rid, cid], ["scheduling.debrief_readiness", rid, cid]] as const;
  const release = useWrite((_: null, key: string) => summaryRelease(input, key), invalidate);
  const notes = useWrite(
    // Edit and Correct each append an active version; the note keeps citing what the last version cited (CR-089)
    (body: { body: string; action: NoteMode }, key: string) => summarySaveNotes({
      ...input, body: body.body, state: "active",
      source_refs: [...(sheet?.notes?.source_refs ?? [])], expected_version: sheet?.notes?.version ?? null,
    }, key),
    invalidate,
  );
  const [error, setError] = useState<string | null>(null);
  const run = (attempt: Promise<unknown>) => { setError(null); attempt.catch((e: unknown) => setError(errorText(e))); };
  if (!sheet) {
    return read.error
      ? <ErrorStrip message={errorText(read.error)} onRetry={isClientRefusal(read.error) ? undefined : () => void read.refetch()} />
      : <Card title={SUMMARY_LABELS.candidateSummary} loading />;
  }
  return (
    <div className="mc-stack">
      {error ? <ErrorStrip message={error} /> : null}
      <SummaryBody
        sheet={sheet}
        check={check.data}
        onRelease={() => run(release.mutateAsync(null))}
        releasing={release.isPending}
        onSaveNotes={(body, action) => run(notes.mutateAsync({ body, action }))}
        savingNotes={notes.isPending}
      />
    </div>
  );
}
